/** Original audit values stay intact; only their presentation is derived here. */
export function auditValue(value: unknown): string {
  if (value == null) return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

export interface ValuePart {
  text: string
  changed: boolean
}

function parts(chars: string[], changed: boolean[]): ValuePart[] {
  return chars.reduce<ValuePart[]>((result, char, index) => {
    const last = result.at(-1)
    if (last && last.changed === changed[index]) last.text += char
    else result.push({ text: char, changed: changed[index] })
    return result
  }, [])
}

/** Character LCS shows insertions without shifting every subsequent character.
 * Large source values use their shared edges to keep rendering bounded. */
export function auditValueDiff(left: string, right: string): [ValuePart[], ValuePart[]] {
  const a = Array.from(left)
  const b = Array.from(right)
  const changedA = a.map(() => true)
  const changedB = b.map(() => true)
  if (a.length * b.length > 250_000) {
    let prefix = 0
    while (prefix < Math.min(a.length, b.length) && a[prefix] === b[prefix]) {
      changedA[prefix] = changedB[prefix] = false
      prefix++
    }
    let i = a.length - 1
    let j = b.length - 1
    while (i >= prefix && j >= prefix && a[i] === b[j]) {
      changedA[i--] = changedB[j--] = false
    }
  } else {
    const rows = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1))
    for (let i = a.length - 1; i >= 0; i--)
      for (let j = b.length - 1; j >= 0; j--)
        rows[i][j] =
          a[i] === b[j] ? rows[i + 1][j + 1] + 1 : Math.max(rows[i + 1][j], rows[i][j + 1])
    let i = 0
    let j = 0
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) changedA[i++] = changedB[j++] = false
      else if (rows[i + 1][j] >= rows[i][j + 1]) i++
      else j++
    }
  }
  return [parts(a, changedA), parts(b, changedB)]
}

export function auditKeyProperties(properties: Record<string, unknown>, preferred: string[]) {
  const keys = [...new Set([...preferred, 'name', 'title', ...Object.keys(properties).sort()])]
  return keys
    .filter(
      key =>
        key in properties &&
        !key.startsWith('_') &&
        !['case_id', 'document_id', 'embedding'].includes(key)
    )
    .slice(0, 5)
    .map(key => [key, properties[key]] as const)
}
